const _ = require('lodash')
const Promise = require('bluebird')
const jsonUtils = require('shared/json-utils')
const sharedUtils = require('shared/utils')
const { ExtendableError } = require('shared/error-utils')

// Configuration for client v1-v2 cross-compatibility
function NOT_CONFIGURED() {
 throw new Error('request lib is not configured!')
}
let csrfToken = null
let buildNumber = null
let getPageName = NOT_CONFIGURED
let getSessionId = NOT_CONFIGURED
let onCsrfError = NOT_CONFIGURED

// Attempt to auto-configure for v1 (wiggin) client
if (typeof wiggin !== 'undefined') {
  configure({
    csrfToken: wiggin._csrf,
    buildNumber: wiggin.buildNumber,
    getPageName: pageContext => _.get(pageContext, 'page_name', app.state('page')),
    getSessionId: () => app.sess.update(),
    onCsrfError: err => app.trigger('userAuthInvalidated', err)
  })
}

function configure(options) {
  if (options.csrfToken) {
    csrfToken = options.csrfToken
  }
  if (options.buildNumber) {
    buildNumber = options.buildNumber
  }
  if (options.getPageName) {
    getPageName = options.getPageName
  }
  if (options.getSessionId) {
    getSessionId = options.getSessionId
  }
  if (options.onCsrfError) {
    onCsrfError = options.onCsrfError
  }
}

function send(options) {
  let req
  const promise = new Promise((resolve, reject) => {
    const ajaxOptions = extendAjaxOptions(options)
    ajaxOptions.success = (response, textStatus, xhr) => {
      if (_.isFunction(options.success)) {
        options.success(response, textStatus, xhr)
      }
      resolve(response)
    }
    ajaxOptions.error = (xhr) => {
      const err = RequestError.fromXHR(options.url, xhr)
      if (_.isFunction(options.error)) {
        options.error(err)
      }
      if (err.message === 'invalid csrf token') {
        onCsrfError(err)
      }
      reject(err)
    }
    req = $.ajax(ajaxOptions)
  })
  promise.req = req
  return promise
}

function extendAjaxOptions(options) {
  // Defer loading analytics lib to handle circular dependency
  const analytics = require('client/lib/analytics')
  let pageContext = analytics.getPageContext()
  // Allow the request data to override the global page context
  let dataPageContext = _.get(options, 'data.page_context')
  if (dataPageContext) {
    dataPageContext = analytics.normalizePageContext(dataPageContext)
    pageContext = _.assign({}, pageContext, dataPageContext)
  }
  const pageName = getPageName(pageContext)

  const ajaxOptions = _.merge({
    dataFilter: jsonUtils.filterHijackProofJSON,
    headers: {
      'x-csrf-token': csrfToken,
      'x-mix-page': pageName,
      'x-language': 'en',
      'x-session-id': getSessionId(),
      'x-build-number': buildNumber
    }
  }, options)

  if (_.includes(['post', 'delete', 'put'], options.method)) {
    if (!_.isArray(options.data)) {
      ajaxOptions.data = _.assign({}, options.data, { 'page_context': pageContext })
    }
    if (options.contentType === 'application/json') {
      ajaxOptions.data = JSON.stringify(ajaxOptions.data)
    }
  }

  return ajaxOptions
}

const READY_STATES = ['UNSENT', 'OPENED', 'HEADERS_RECEIVED', 'LOADING', 'DONE']

function readyStateString(readyState) {
  for (let i = 0, len = READY_STATES.length; i < len; i++) {
    if (readyState === i) {
      return READY_STATES[i]
    }
  }
  return 'UNKNOWN'
}

function responseJSON(xhr) {
  if (xhr.responseJSON) {
    return xhr.responseJSON
  }
  const contentType = xhr.getResponseHeader('content-type')
  if (contentType && contentType.startsWith('application/json')) {
    return JSON.parse(xhr.responseText)
  }
}

function isFriendlyError(xhr) {
  const response = responseJSON(xhr)
  return _.has(response, 'error.friendlyMessage') || _.has(response, 'err.friendlyMessage')
}

class RequestError extends ExtendableError {
  constructor(url, xhr) {
    let message = RequestError.message(url, xhr)
    if (_.isObject(message)) {
      message = JSON.stringify(message)
    }
    super(message)
    Object.assign(this, { url, xhr })
  }

  static fromXHR(url, xhr) {
    if (isFriendlyError(xhr)) {
      return new FriendlyRequestError(url, xhr)
    }
    return new RequestError(url, xhr)
  }

  get isTimeout() {
    return this.xhr.statusText === 'timeout'
  }

  get isIgnorable() {
    const isRequestInternal = !sharedUtils.isExternalUrl(this.url)
    const isRequestSent = this.xhr.readyState === READY_STATES.indexOf('UNSENT')
    return (
      // Aborted requests are not really errors.
      this.xhr.statusText === 'abort' ||
      // Unsent requests generally mean client-side network hiccups.
      (isRequestInternal && this.xhr.status === 0 && !isRequestSent) ||
      // Don't kick ourselves while we're down.
      (isRequestInternal && this.xhr.status >= 500)
    )
  }

  static message(url, xhr) {
    const responseObj = responseJSON(xhr)
    const { readyState, status, statusText, responseText } = xhr
    const responseError = _.get(responseObj, 'error') || _.get(responseObj, 'err', responseText)
    if (responseError && !_.startsWith(responseError, '<!DOCTYPE html>')) {
      return responseError
    }
    if (status) {
      return `XHR status[${status}] for url[${url}]`
    }
    if (statusText && statusText !== 'error') {
      return `XHR statusText[${statusText}] for url[${url}]`
    }
    return `XHR readyState[${readyStateString(readyState)}] for url[${url}]`
  }
}

class FriendlyRequestError extends RequestError {
  constructor(url, xhr) {
    super(url, xhr)

    const message = RequestError.message(url, xhr)
    if (_.has(message, 'friendlyMessage')) {
      this.requestId = message.requestId
      this.statusCode = message.statusCode
      this.message =  message.friendlyMessage
    } else if (_.isObject(message)) {
      this.message = JSON.stringify(message)
    }
  }
}

module.exports = {
  // Classes
  RequestError,
  FriendlyRequestError,

  // Functions
  isFriendlyError,
  configure,
  send
}
